/**
 * Admin Prompts Page
 *
 * Manage prompt templates for different LLM operations:
 * - extraction: Knowledge extraction from sources
 * - voice: Voice profile generation
 * - post_generation: LinkedIn post generation
 * - summarization: Content summarization
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../platform';
import { apiRequest } from '../platform/api';
import {
  FileText,
  Plus,
  Edit2,
  Check,
  Loader2,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Zap,
  MessageSquare,
  FileSearch,
} from 'lucide-react';

// Types
interface PromptTemplate {
  id: string;
  name: string;
  type: 'extraction' | 'voice' | 'post_generation' | 'summarization';
  version: string;
  system_prompt: string | null;
  user_prompt_template: string;
  output_schema: Record<string, unknown> | null;
  model: string;
  temperature: number;
  max_tokens: number;
  is_active: boolean;
  created_at: string;
  created_by: string | null;
}

const PROMPT_TYPES = [
  { value: 'extraction', label: 'Knowledge Extraction', icon: FileSearch, color: 'blue' },
  { value: 'voice', label: 'Voice Profile', icon: MessageSquare, color: 'purple' },
  { value: 'post_generation', label: 'Post Generation', icon: Sparkles, color: 'green' },
  { value: 'summarization', label: 'Summarization', icon: Zap, color: 'orange' },
] as const;

const MODELS = [
  { value: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4' },
  { value: 'claude-opus-4-20250514', label: 'Claude Opus 4' },
  { value: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet' },
  { value: 'claude-3-opus-20240229', label: 'Claude 3 Opus' },
];

// Module-level loading tracker to survive component remounts caused by auth context changes
let adminPromptsLastLoadTime = 0;
let adminPromptsLoadingPromise: Promise<PromptTemplate[] | null> | null = null;

export function AdminPromptsPage() {
  const { isAuthenticated } = useAuth();

  const [prompts, setPrompts] = useState<PromptTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Edit/Create state
  const [isEditing, setIsEditing] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<Partial<PromptTemplate> | null>(null);
  const [saving, setSaving] = useState(false);

  // Track if this instance has processed the load result
  const hasProcessedRef = useRef(false);

  // Load prompts - used for refresh after save
  const loadPrompts = useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiRequest<{ data: PromptTemplate[] }>('/api/admin/prompts');
      setPrompts(result.data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load prompts');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load - deduplicated across remounts using module-level state
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // If this instance already processed the result, skip
    if (hasProcessedRef.current) {
      return;
    }

    const now = Date.now();

    // If a load started recently (within 5 seconds), reuse that promise
    if (adminPromptsLoadingPromise && now - adminPromptsLastLoadTime < 5000) {
      hasProcessedRef.current = true;
      adminPromptsLoadingPromise.then((result) => {
        if (result) {
          setPrompts(result);
        }
        setLoading(false);
      });
      return;
    }

    // Start a new load
    adminPromptsLastLoadTime = now;
    hasProcessedRef.current = true;

    const doLoad = async (): Promise<PromptTemplate[] | null> => {
      try {
        const result = await apiRequest<{ data: PromptTemplate[] }>('/api/admin/prompts');
        return result.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load prompts');
        return null;
      }
    };

    adminPromptsLoadingPromise = doLoad();

    adminPromptsLoadingPromise.then((result) => {
      if (result) {
        setPrompts(result);
      }
      setLoading(false);
    });
  }, [isAuthenticated]);

  const handleCreate = () => {
    setEditingPrompt({
      name: '',
      type: 'post_generation',
      version: '1.0.0',
      system_prompt: '',
      user_prompt_template: '',
      model: 'claude-sonnet-4-20250514',
      temperature: 0.7,
      max_tokens: 4096,
      is_active: false,
    });
    setIsEditing(true);
  };

  const handleEdit = (prompt: PromptTemplate) => {
    setEditingPrompt({ ...prompt });
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!editingPrompt) return;

    setSaving(true);
    setError(null);

    try {
      if (editingPrompt.id) {
        // Update existing
        await apiRequest(`/api/admin/prompts/${editingPrompt.id}`, {
          method: 'PATCH',
          body: editingPrompt,
        });
      } else {
        // Create new
        await apiRequest('/api/admin/prompts', {
          method: 'POST',
          body: editingPrompt,
        });
      }

      await loadPrompts();
      setIsEditing(false);
      setEditingPrompt(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save prompt');
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (id: string) => {
    try {
      await apiRequest(`/api/admin/prompts/${id}/activate`, { method: 'POST' });
      await loadPrompts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to activate prompt');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingPrompt(null);
  };

  const updateEditingPrompt = (field: string, value: unknown) => {
    setEditingPrompt(prev => prev ? { ...prev, [field]: value } : null);
  };

  // Filter prompts
  const filteredPrompts = filterType === 'all'
    ? prompts
    : prompts.filter(p => p.type === filterType);

  // Group by type
  const groupedPrompts = filteredPrompts.reduce((acc, prompt) => {
    if (!acc[prompt.type]) acc[prompt.type] = [];
    acc[prompt.type].push(prompt);
    return acc;
  }, {} as Record<string, PromptTemplate[]>);

  const getTypeConfig = (type: string) => {
    return PROMPT_TYPES.find(t => t.value === type) || PROMPT_TYPES[0];
  };

  if (loading && !prompts.length) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Prompt Templates</h1>
          <p className="text-gray-500 mt-1">Manage AI prompts for content generation</p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-mazo-600 text-white rounded-lg hover:bg-mazo-700"
        >
          <Plus className="h-4 w-4" />
          New Prompt
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        <button
          onClick={() => setFilterType('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
            filterType === 'all'
              ? 'bg-gray-900 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          All Types
        </button>
        {PROMPT_TYPES.map(type => {
          const Icon = type.icon;
          return (
            <button
              key={type.value}
              onClick={() => setFilterType(type.value)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                filterType === type.value
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Icon className="h-4 w-4" />
              {type.label}
            </button>
          );
        })}
      </div>

      {/* Edit/Create Modal */}
      {isEditing && editingPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold">
                {editingPrompt.id ? 'Edit Prompt Template' : 'Create Prompt Template'}
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={editingPrompt.name || ''}
                    onChange={(e) => updateEditingPrompt('name', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-500 focus:border-mazo-500"
                    placeholder="e.g., LinkedIn Post Generator v1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Version</label>
                  <input
                    type="text"
                    value={editingPrompt.version || ''}
                    onChange={(e) => updateEditingPrompt('version', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-500 focus:border-mazo-500"
                    placeholder="1.0.0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={editingPrompt.type || 'post_generation'}
                    onChange={(e) => updateEditingPrompt('type', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-500 focus:border-mazo-500"
                  >
                    {PROMPT_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                  <select
                    value={editingPrompt.model || 'claude-sonnet-4-20250514'}
                    onChange={(e) => updateEditingPrompt('model', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-500 focus:border-mazo-500"
                  >
                    {MODELS.map(model => (
                      <option key={model.value} value={model.value}>{model.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Temperature ({editingPrompt.temperature})
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={editingPrompt.temperature || 0.7}
                    onChange={(e) => updateEditingPrompt('temperature', parseFloat(e.target.value))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Tokens</label>
                  <input
                    type="number"
                    value={editingPrompt.max_tokens || 4096}
                    onChange={(e) => updateEditingPrompt('max_tokens', parseInt(e.target.value))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-500 focus:border-mazo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">System Prompt</label>
                <textarea
                  value={editingPrompt.system_prompt || ''}
                  onChange={(e) => updateEditingPrompt('system_prompt', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-500 focus:border-mazo-500 font-mono text-sm"
                  placeholder="You are an expert content writer..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  User Prompt Template
                  <span className="text-gray-400 font-normal ml-2">
                    (Use {'{{variable}}'} for placeholders)
                  </span>
                </label>
                <textarea
                  value={editingPrompt.user_prompt_template || ''}
                  onChange={(e) => updateEditingPrompt('user_prompt_template', e.target.value)}
                  rows={8}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-500 focus:border-mazo-500 font-mono text-sm"
                  placeholder="Write a LinkedIn post about {{topic}} based on the following knowledge:&#10;&#10;{{knowledge_items}}&#10;&#10;Voice profile: {{voice_profile}}"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={editingPrompt.is_active || false}
                  onChange={(e) => updateEditingPrompt('is_active', e.target.checked)}
                  className="h-4 w-4 text-mazo-600 rounded"
                />
                <label htmlFor="is_active" className="text-sm text-gray-700">
                  Set as active (will deactivate other prompts of the same type)
                </label>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !editingPrompt.name || !editingPrompt.user_prompt_template}
                className="flex items-center gap-2 px-4 py-2 bg-mazo-600 text-white rounded-lg hover:bg-mazo-700 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4" />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Prompts List */}
      {Object.entries(groupedPrompts).map(([type, typePrompts]) => {
        const config = getTypeConfig(type);
        const Icon = config.icon;

        return (
          <div key={type} className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Icon className={`h-5 w-5 text-${config.color}-600`} />
              <h2 className="text-lg font-semibold">{config.label}</h2>
              <span className="text-sm text-gray-400">({typePrompts.length})</span>
            </div>

            <div className="space-y-3">
              {typePrompts.map(prompt => (
                <div
                  key={prompt.id}
                  className={`border rounded-lg ${
                    prompt.is_active
                      ? 'border-green-200 bg-green-50'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div
                    className="p-4 flex items-center justify-between cursor-pointer"
                    onClick={() => setExpandedId(expandedId === prompt.id ? null : prompt.id)}
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-gray-400" />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{prompt.name}</span>
                          <span className="text-xs px-2 py-0.5 bg-gray-100 rounded text-gray-500">
                            v{prompt.version}
                          </span>
                          {prompt.is_active && (
                            <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded font-medium">
                              Active
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          {prompt.model} · temp: {prompt.temperature}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {!prompt.is_active && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleActivate(prompt.id);
                          }}
                          className="px-3 py-1.5 text-sm text-green-600 hover:bg-green-100 rounded-lg"
                        >
                          Activate
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(prompt);
                        }}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      {expandedId === prompt.id ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {expandedId === prompt.id && (
                    <div className="px-4 pb-4 space-y-3 border-t border-gray-100">
                      {prompt.system_prompt && (
                        <div className="mt-4">
                          <label className="text-xs font-medium text-gray-500 uppercase">
                            System Prompt
                          </label>
                          <pre className="mt-1 p-3 bg-gray-100 rounded-lg text-sm font-mono whitespace-pre-wrap overflow-x-auto max-h-40 overflow-y-auto">
                            {prompt.system_prompt}
                          </pre>
                        </div>
                      )}
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase">
                          User Prompt Template
                        </label>
                        <pre className="mt-1 p-3 bg-gray-100 rounded-lg text-sm font-mono whitespace-pre-wrap overflow-x-auto max-h-60 overflow-y-auto">
                          {prompt.user_prompt_template}
                        </pre>
                      </div>
                      <div className="text-xs text-gray-400">
                        Created: {new Date(prompt.created_at).toLocaleString()}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {filteredPrompts.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>No prompt templates found</p>
          <button
            onClick={handleCreate}
            className="mt-4 text-mazo-600 hover:text-mazo-700"
          >
            Create your first prompt
          </button>
        </div>
      )}
    </div>
  );
}

export default AdminPromptsPage;
